import { useState, useEffect } from 'react';
import { Paper, Title, Stack, TextInput, Button, Group, Text, Loader, Center, Alert } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSettings, IconInfoCircle } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/api/client';

interface Settings {
  'matomo-token'?: string;
  'matomo-url'?: string;
  'matomo-site-id'?: string;
}

function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: async (): Promise<Settings> => {
      const res = await apiClient.get('/settings');
      return res.data;
    },
  });
}

function useSaveSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: Partial<Settings>) => {
      const res = await apiClient.put('/settings', settings);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['cockpit', 'analytics'] });
      notifications.show({ title: 'Gespeichert', message: 'Einstellungen wurden aktualisiert.', color: 'green' });
    },
    onError: (err: any) => {
      notifications.show({ title: 'Fehler', message: err.response?.data?.error || err.message, color: 'red' });
    },
  });
}

export function SettingsPage() {
  const { data, isLoading } = useSettings();
  const saveSettings = useSaveSettings();

  const [matomoToken, setMatomoToken] = useState('');
  const [matomoUrl, setMatomoUrl] = useState('');
  const [matomoSiteId, setMatomoSiteId] = useState('');

  useEffect(() => {
    if (data) {
      setMatomoToken(data['matomo-token'] ?? '');
      setMatomoUrl(data['matomo-url'] ?? 'https://matomo.u-like-it.de/index.php');
      setMatomoSiteId(data['matomo-site-id'] ?? '1');
    }
  }, [data]);

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Title order={2}>Einstellungen</Title>
        <Center><Loader /></Center>
      </Stack>
    );
  }

  const handleSave = () => {
    saveSettings.mutate({
      'matomo-token': matomoToken,
      'matomo-url': matomoUrl,
      'matomo-site-id': matomoSiteId,
    });
  };

  return (
    <Stack gap="lg">
      <Title order={2}>Einstellungen</Title>

      <Paper shadow="xs" p="md" withBorder>
        <Group gap="xs" mb="md">
          <IconSettings size={20} />
          <Title order={4}>Matomo Analytics</Title>
        </Group>

        <Alert icon={<IconInfoCircle size={16} />} color="blue" mb="md">
          Der API-Token wird für das Cockpit-Dashboard benötigt. Du findest ihn in Matomo unter
          Einstellungen &rarr; Persönlich &rarr; Sicherheit &rarr; Neuen Token erstellen.
        </Alert>

        <Stack gap="sm">
          <TextInput
            label="API Token"
            description="Matomo Auth-Token für API-Zugriff"
            placeholder="z.B. a1b2c3d4e5f6..."
            value={matomoToken}
            onChange={(e) => setMatomoToken(e.currentTarget.value)}
          />
          <TextInput
            label="Matomo URL"
            description="Basis-URL der Matomo API"
            placeholder="https://matomo.u-like-it.de/index.php"
            value={matomoUrl}
            onChange={(e) => setMatomoUrl(e.currentTarget.value)}
          />
          <TextInput
            label="Site ID"
            description="Matomo Website-ID"
            placeholder="1"
            value={matomoSiteId}
            onChange={(e) => setMatomoSiteId(e.currentTarget.value)}
          />
        </Stack>

        <Group justify="flex-end" mt="md">
          <Button onClick={handleSave} loading={saveSettings.isPending}>
            Speichern
          </Button>
        </Group>

        {!matomoToken && (
          <Text size="sm" c="orange" mt="sm">
            Kein Token konfiguriert. Das Matomo-Panel im Cockpit wird keine Daten anzeigen.
          </Text>
        )}
      </Paper>
    </Stack>
  );
}
